import { useEffect, useRef } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

/**
 * Auto-logout after a period of inactivity.
 *
 * - Resets a timer on any user activity (mouse/keyboard/touch/scroll).
 * - Fires `onIdle` once the timeout elapses with no activity.
 * - Throttles timer resets so high-frequency events (mousemove/scroll) don't
 *   thrash; activity is recorded in a ref and the timer only re-arms periodically.
 *
 * Purely client-side hygiene for shared/family devices — pairs with the
 * server-side "revoke prior sessions on login" so an abandoned tab can't keep a
 * session alive.
 */
export function useIdleLogout({ timeoutMs = 30 * 60 * 1000, enabled = true, onIdle }) {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    if (!enabled) return undefined;

    lastActivityRef.current = Date.now();
    const markActivity = () => { lastActivityRef.current = Date.now(); };

    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, markActivity, { passive: true })
    );

    // Poll every 30s and log out once the idle window is exceeded.
    const intervalId = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= timeoutMs) {
        onIdleRef.current?.();
      }
    }, 30 * 1000);

    return () => {
      clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActivity));
    };
  }, [timeoutMs, enabled]);
}
